import React, { useState, useEffect } from 'react';
import { BrainCircuit, UploadCloud, FileText, CheckCircle2, AlertTriangle, Cpu, Database, Search, ArrowRight, Lock, Fingerprint, Zap, Building2, Briefcase } from 'lucide-react';
import { generateDeepAudit } from '../services/geminiService';
import { AuditResult } from '../types';

const DeepAudit: React.FC = () => {
  // STATE
  const [companyName, setCompanyName] = useState("Müller Logistik GmbH");
  const [industry, setIndustry] = useState("Transport & Logistik");
  
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [tokensProcessed, setTokensProcessed] = useState(0);
  const [stage, setStage] = useState<'IDLE' | 'UPLOADING' | 'VECTOREMBEDDING' | 'REASONING' | 'DONE'>('IDLE');
  
  // Real AI Result
  const [auditResult, setAuditResult] = useState<AuditResult | null>(null);

  useEffect(() => {
    if (isAnalyzing && progress < 100) {
        const timer = setInterval(() => {
            setProgress(prev => {
                const increment = 0.5; // Speed of animation
                let newProg = prev + increment;
                
                // STAGE MAPPING
                if (newProg < 20) setStage('UPLOADING');
                else if (newProg < 50) setStage('VECTOREMBEDDING');
                else if (newProg < 90) setStage('REASONING');
                
                // INTELLIGENT BLOCKING:
                // Wenn wir bei 85% sind ("Reasoning") und noch kein AI Ergebnis haben, warten wir.
                // Das simuliert das "Nachdenken" des Gemini 3.0 Pro Modells perfekt.
                if (newProg > 85 && !auditResult) {
                    return 85 + (Math.random() * 2); // Kleines Wackeln bei 85-87%
                }

                // Wenn Ergebnis da ist, darf es fertig laden
                if (auditResult && newProg >= 100) {
                    setStage('DONE');
                    clearInterval(timer);
                    setIsAnalyzing(false);
                    return 100;
                }
                
                // Token Simulation
                setTokensProcessed(Math.floor((newProg / 100) * 2100000));
                
                return newProg;
            });
        }, 50);
        return () => clearInterval(timer);
    }
  }, [isAnalyzing, auditResult]);

  const startAnalysis = async () => {
      if (!companyName || !industry) return;
      
      setIsAnalyzing(true);
      setAuditResult(null); // Reset previous result
      setProgress(0);
      setTokensProcessed(0);
      
      try {
          // REAL CALL TO GEMINI 3.0 PRO
          console.log(`Starting Audit for ${companyName}...`);
          const result = await generateDeepAudit(companyName, industry);
          setAuditResult(result);
      } catch (error) {
          console.error("Deep Audit Failed", error);
          setIsAnalyzing(false);
          // Error handling could be added here
      }
  };

  return (
    <div className="animate-fade-in h-full flex flex-col space-y-8">
      <header className="flex justify-between items-center">
        <div>
            <h2 className="text-3xl font-bold text-white tracking-tight flex items-center">
                <BrainCircuit className="w-8 h-8 mr-3 text-purple-500" />
                Orbit Deep Audit™
            </h2>
            <p className="text-slate-400">Powered by Gemini 3.0 Pro • 2M Token Context • High-Reasoning Engine</p>
        </div>
        <div className="bg-purple-900/20 text-purple-400 border border-purple-500/30 px-4 py-2 rounded-full text-xs font-bold flex items-center animate-pulse">
            <Zap className="w-3 h-3 mr-2 fill-current" />
            LIVE AI CONNECTED
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 flex-1">
          {/* LEFT: INPUT PANEL */}
          <div className="lg:col-span-1 space-y-6">
              <div className="bg-slate-800 border border-slate-700 p-6 rounded-2xl shadow-xl relative overflow-hidden group">
                  <div className="absolute top-0 right-0 p-4 opacity-10">
                      <UploadCloud className="w-24 h-24 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-6">Unternehmens-Daten</h3>
                  
                  <div className="space-y-4 mb-6">
                      <div>
                          <label className="text-xs text-slate-500 uppercase font-bold mb-1 ml-1">Firmenname</label>
                          <div className="flex items-center bg-slate-900 border border-slate-600 rounded-xl px-4 py-3 focus-within:border-purple-500 transition-colors">
                              <Building2 className="w-5 h-5 text-slate-400 mr-3" />
                              <input 
                                type="text" 
                                value={companyName} 
                                onChange={(e) => setCompanyName(e.target.value)}
                                className="bg-transparent text-white w-full outline-none font-medium"
                              />
                          </div>
                      </div>
                      
                      <div>
                          <label className="text-xs text-slate-500 uppercase font-bold mb-1 ml-1">Branche / Fokus</label>
                          <div className="flex items-center bg-slate-900 border border-slate-600 rounded-xl px-4 py-3 focus-within:border-purple-500 transition-colors">
                              <Briefcase className="w-5 h-5 text-slate-400 mr-3" />
                              <input 
                                type="text" 
                                value={industry} 
                                onChange={(e) => setIndustry(e.target.value)}
                                className="bg-transparent text-white w-full outline-none font-medium"
                              />
                          </div>
                      </div>
                  </div>

                  <div className="border-2 border-dashed border-slate-600 rounded-xl p-6 text-center hover:border-purple-500 hover:bg-slate-700/30 transition-all cursor-pointer group-hover:scale-[1.01]">
                      <FileText className="w-8 h-8 mx-auto text-slate-500 mb-2 group-hover:text-purple-400" />
                      <p className="text-slate-300 font-medium text-sm">Zusatz-Dokumente (Optional)</p>
                      <p className="text-[10px] text-purple-400 mt-2 font-mono">Limit: 2M Tokens (Gemini 3.0)</p>
                  </div>

                  <button 
                    onClick={startAnalysis}
                    disabled={isAnalyzing}
                    className="w-full mt-6 bg-purple-600 hover:bg-purple-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-bold py-4 rounded-xl shadow-lg shadow-purple-900/40 transition-all flex items-center justify-center border border-purple-400/20"
                  >
                      {isAnalyzing ? (
                          <>
                            <Cpu className="w-5 h-5 mr-2 animate-spin" />
                            AI Reasoning...
                          </>
                      ) : (
                          <>
                            <Search className="w-5 h-5 mr-2" />
                            Analyse Starten
                          </>
                      )}
                  </button>
              </div>
          </div>

          {/* RIGHT: VISUALIZATION PANEL */}
          <div className="lg:col-span-2 flex flex-col">
              <div className="bg-black border border-slate-800 rounded-2xl p-6 flex-1 relative overflow-hidden flex flex-col shadow-2xl">
                  {/* Grid Background */}
                  <div className="absolute inset-0 opacity-20" 
                       style={{ backgroundImage: 'linear-gradient(#333 1px, transparent 1px), linear-gradient(90deg, #333 1px, transparent 1px)', backgroundSize: '20px 20px' }}>
                  </div>

                  {stage === 'IDLE' && (
                      <div className="flex-1 flex flex-col items-center justify-center text-center z-10 opacity-50">
                          <BrainCircuit className="w-24 h-24 text-purple-900 mb-6" />
                          <h3 className="text-2xl font-bold text-slate-500">System Ready</h3>
                          <p className="text-slate-600 max-w-md mt-2">Warte auf Input zur Generierung des Vektorraums. Gemini 3.0 Pro Verbindung steht bereit.</p>
                      </div>
                  )}

                  {(stage !== 'IDLE') && (
                      <div className="z-10 flex-1 flex flex-col relative">
                          {/* Header Stats */}
                          <div className="flex justify-between items-end mb-6 border-b border-slate-800 pb-4">
                              <div>
                                  <div className="text-xs text-purple-400 font-mono mb-1">TOKENS PROCESSED</div>
                                  <div className="text-4xl font-mono font-bold text-white tabular-nums">
                                      {tokensProcessed.toLocaleString()}
                                  </div>
                              </div>
                              <div className="text-right">
                                  <div className="text-xs text-slate-500 font-mono mb-1">STATUS</div>
                                  <div className="text-xl font-bold text-white flex items-center justify-end uppercase tracking-wider">
                                      {stage === 'UPLOADING' && <span className="text-blue-400 animate-pulse">Ingesting...</span>}
                                      {stage === 'VECTOREMBEDDING' && <span className="text-purple-400 animate-pulse">Vectorizing...</span>}
                                      {stage === 'REASONING' && <span className="text-orange-400 animate-pulse">AI Reasoning...</span>}
                                      {stage === 'DONE' && <span className="text-emerald-400">Audit Complete</span>}
                                  </div>
                              </div>
                          </div>

                          {/* SCROLLING LOGS / RESULTS */}
                          <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
                              {stage !== 'DONE' ? (
                                  <div className="font-mono text-xs text-green-500/80 space-y-1">
                                      <div className="opacity-50">&gt; Initializing Gemini 3.0 Pro Context Window...</div>
                                      {progress > 5 && <div>&gt; Parsing Company Entity: "{companyName}"...</div>}
                                      {progress > 10 && <div>&gt; Accessing Industry Knowledge Base: "{industry}"...</div>}
                                      {progress > 20 && <div>&gt; Generating Embeddings (Dim: 1536)... [OK]</div>}
                                      {progress > 40 && <div>&gt; Analyzing Workflow Efficiency Patterns...</div>}
                                      {progress > 60 && <div className="text-purple-400">&gt; Thinking Block Active: Validating Assumptions...</div>}
                                      {progress > 80 && <div className="text-orange-400 animate-pulse">&gt; Gemini is reasoning (This may take a moment)...</div>}
                                  </div>
                              ) : (
                                  // REAL RESULT DISPLAY
                                  <div className="animate-fade-in space-y-6">
                                      <div className="bg-slate-900/80 border border-emerald-500/30 p-6 rounded-xl backdrop-blur-md">
                                          <div className="flex justify-between items-start mb-4">
                                              <div>
                                                  <h2 className="text-2xl font-bold text-white mb-1">Automation Score</h2>
                                                  <p className="text-slate-400 text-sm">Basierend auf 2.1 Mio Datenpunkten</p>
                                              </div>
                                              <div className="text-5xl font-black text-emerald-400 shadow-emerald-500/20 drop-shadow-lg">
                                                  {auditResult?.score}/100
                                              </div>
                                          </div>
                                          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                                              <div className="h-full bg-emerald-500" style={{ width: `${auditResult?.score}%` }}></div>
                                          </div>
                                      </div>

                                      <div className="grid grid-cols-2 gap-4">
                                          <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700">
                                              <h4 className="text-slate-400 text-xs font-bold uppercase mb-2">Geschätztes Sparpotenzial</h4>
                                              <div className="text-2xl font-bold text-white">{auditResult?.estimatedSavings}</div>
                                          </div>
                                          <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700">
                                              <h4 className="text-slate-400 text-xs font-bold uppercase mb-2">Risiko-Analyse</h4>
                                              <div className="text-white text-sm">
                                                  {auditResult?.risks[0] || "Keine kritischen Risiken"}
                                              </div>
                                          </div>
                                      </div>

                                      <div className="space-y-4">
                                          <h4 className="text-white font-bold flex items-center">
                                              <CheckCircle2 className="w-5 h-5 text-purple-500 mr-2" /> 
                                              Empfohlene Automatisierungen
                                          </h4>
                                          {auditResult?.opportunities.map((opp, i) => (
                                              <div key={i} className="flex items-start p-3 bg-purple-900/10 border border-purple-500/20 rounded-lg">
                                                  <div className="text-purple-400 font-bold mr-3">{i+1}.</div>
                                                  <div className="text-slate-300 text-sm">{opp}</div>
                                              </div>
                                          ))}
                                      </div>
                                      
                                      <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700">
                                          <h4 className="text-xs text-slate-500 font-bold uppercase mb-2">Executive Summary</h4>
                                          <p className="text-sm text-slate-300 italic leading-relaxed">
                                              "{auditResult?.summary}"
                                          </p>
                                      </div>
                                  </div>
                              )}
                          </div>
                      </div>
                  )}

                  <div className="absolute bottom-4 right-4 flex items-center space-x-4 opacity-50 z-20">
                      <div className="flex items-center text-[10px] text-slate-500">
                          <Lock className="w-3 h-3 mr-1" /> End-to-End Encryption
                      </div>
                      <div className="flex items-center text-[10px] text-slate-500">
                          <Fingerprint className="w-3 h-3 mr-1" /> On-Premise Processing
                      </div>
                  </div>
              </div>
          </div>
      </div>
    </div>
  );
};

export default DeepAudit;